/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { signUp_DLXAsync } from '../../actions/DataActionExtension.g';
import {
    determineSiteLanguageForAbandonedCart,
    getSiteLanguageConfigFromContext
} from '../../themes/venchi-v2-1/utils/determineSiteLanguageForAbandonedCart';
import { INewsletterBlockViewProps } from './newsletter-block';
import classnames from 'classnames';
import { zodResolver } from '@hookform/resolvers/zod';
import { newsletterSubscriptionSchema } from '../../validation/zod-schemas/forms/newsletter-subscription';

export interface ExternalPayload {
    email: string;
    marketing: boolean;
    profiling: boolean;
    thirdparty: boolean;
    message?: string;
}

const NewsletterBlockView: React.FC<INewsletterBlockViewProps> = props => {
    const schema = newsletterSubscriptionSchema;

    type Inputs = {
        email: string;
        marketingEmails: boolean;
        profiling: boolean;
        thirdParty: boolean;
    };
    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<Inputs>({
        resolver: zodResolver(schema),
        mode: 'onChange'
    });
    const [loading, setLoading] = React.useState(false);
    const [subscribeMessage, setSubscribeMessage] = React.useState({ state: false, message: '', class: '' });

    const onSubmit = async (data: Inputs) => {
        setLoading(true);
        try {
            let source: string;
            switch (props.config.source?.toString()) {
                case 'CommerceSubscribersAU':
                    source = 'Commerce Subscriber AU';
                    break;
                case 'CommerceSubscriberEU':
                    source = 'Commerce Subscriber EU';
                    break;
                case 'CommerceSubscribersUS':
                    source = 'Commerce Subscribers US';
                    break;
                case 'CommerceSubscriberSG':
                    source = 'Commerce Subscriber SG';
                    break;
                case 'CommerceSubscriberHK':
                    source = 'Commerce Subscriber HK';
                    break;
                case 'RetailEU':
                    source = 'Retail EU';
                    break;
                case 'RetailUS':
                    source = 'Retail US';
                    break;
                default:
                    source = '';
            }

            const customExternalAPI = props?.config?.customExternalAPI;
            const externalApiURL = props.config.externalApiURL ?? '';
            const externalThankYouMessage = props.config.externalThankYouMessage ?? '';

            const siteLanguageConfig = getSiteLanguageConfigFromContext(props.context);
            const CRMRegion = determineSiteLanguageForAbandonedCart(siteLanguageConfig);

            const _subscribeToNL = async (data: Inputs) => {
                setLoading(true);
                try {
                    const result = await signUp_DLXAsync(
                        { callerContext: props.context?.actionContext },
                        '',
                        '',
                        data.email,
                        '',
                        false,
                        data.marketingEmails,
                        data.profiling,
                        data.thirdParty,
                        props.context.request.locale,
                        '',
                        '',
                        source,
                        CRMRegion
                    );

                    const message = result.Error
                        ? result.Error === 'already_subscribed'
                            ? props.resources.alreadySubscribedText
                            : result.Error
                        : props.resources.verifyEmailText;

                    setSubscribeMessage({ state: true, message, class: 'success' });
                } catch (error) {
                    setSubscribeMessage({ state: true, message: (error as Error).message, class: 'error' });
                } finally {
                    setLoading(false);
                }
            };

            const postJson = (url: string, data: any): Promise<number> => {
                return fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(data)
                })
                    .then(response => {
                        if (!response.ok) {
                            throw new Error(`HTTP error! Status: ${response.status}`);
                        }
                        return response.status;
                    })
                    .catch(error => {
                        console.error('Error in newsletter-block.view.tsx - postJson:', error);
                        throw error;
                    });
            };

            const _externalSubscribeToNL = async (data: Inputs) => {
                setLoading(true);
                const payload: ExternalPayload = {
                    email: data.email,
                    marketing: data.marketingEmails,
                    profiling: data.profiling,
                    thirdparty: data.thirdParty
                };
                try {
                    await postJson(externalApiURL, payload);
                    setSubscribeMessage({ state: true, message: externalThankYouMessage, class: 'success' });
                } catch (error) {
                    setSubscribeMessage({ state: true, message: (error as Error).message, class: 'error' });
                } finally {
                    setLoading(false);
                }
            };

            if (customExternalAPI) {
                await _externalSubscribeToNL(data);
            } else {
                await _subscribeToNL(data);
            }
        } catch (error) {
            setSubscribeMessage({ state: true, message: (error as Error).message, class: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className={classnames(props.className, 'container')}
            id={props.id}
            style={props.config.backgroundImage?.src ? { backgroundImage: `url(${props.config.backgroundImage.src})` } : undefined}
            {...props.renderModuleAttributes(props)}
        >
            {props.paragraph}
            {loading && <div className='loading-spinner'>Loading...</div>}
            {subscribeMessage.state && (
                <div className={`msc-alert msc-alert-${subscribeMessage.class}`} role='alert'>
                    {subscribeMessage.message}
                </div>
            )}
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className={classnames('email_row row msc-address-form__item', { 'address-form__item-invalid': errors.email })}>
                    <input
                        name='email'
                        type='text'
                        ref={register}
                        placeholder={props.resources.yourEmailAddressText}
                        maxLength={320}
                        disabled={loading}
                        aria-invalid={errors.email ? 'true' : 'false'}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                    <button type='submit' disabled={loading} className={`status__`}>
                        <span>{props.resources.subscribeBtnText}</span>
                    </button>
                    {errors?.email?.message && (
                        <span id='email-error' className='msc-address-form__alert' role='alert'>
                            {errors.email.message}
                        </span>
                    )}
                </div>
                <div className={'privacy_row row'}>
                    <div className={'inputBox privacy_field_ctn'} aria-invalid={errors.marketingEmails ? 'true' : 'false'}>
                        <label>
                            <input
                                ref={register}
                                type={'checkbox'}
                                name={'marketingEmails'}
                                defaultChecked={props.config.preflagMarketing ?? false}
                                hidden={props.config.hideMarketing ?? false}
                                disabled={loading}
                            />
                            {props.privacyTextM}
                            {errors.marketingEmails && (
                                <p className='msc-alert msc-alert-danger' role='alert'>
                                    {props.config.requiredMarketingError ?? 'This checkbox is required'}
                                </p>
                            )}
                        </label>
                    </div>
                </div>
                <div className={`privacy_row row row--PrivacyP ${props.config.hideProfiling && 'hidden'}`}>
                    <div className={'inputBox privacy_field_ctn'}>
                        <label>
                            <input
                                ref={register}
                                type={'checkbox'}
                                name={'profiling'}
                                defaultChecked={props.config.preflagProfiling ?? false}
                                disabled={loading}
                            />
                            {props.privacyTextP}
                        </label>
                    </div>
                </div>
                <div className={`privacy_row row row--PrivacyT ${props.config.hideThirdParty && 'hidden'}`}>
                    <div className={'inputBox privacy_field_ctn'}>
                        <label>
                            <input
                                ref={register}
                                type={'checkbox'}
                                name={'thirdParty'}
                                defaultChecked={props.config.preflagThirdParty ?? false}
                                disabled={loading}
                            />
                            {props.privacyTextT}
                        </label>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default NewsletterBlockView;
